import { useFeatureFlags } from '@chronosphereio/core';
import { useDebounce } from '@chronosphereio/shared';
import type { GridComponentOption } from 'echarts';
import { merge } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChartInstance,
  MouseEventsParameters,
  TimeSeriesTooltipProvider,
  useChartsTheme,
  useId,
  ZoomEventData,
} from '@/components';
import { SeriesActionProvider } from '@/components/TimeSeriesTooltip/SeriesActionProvider';
import {
  DEFAULT_LEGEND,
  EventPayload,
  getLegendMode,
  isLogScale,
  isStackEnabled,
  LegendSpecOptions,
  resolveSyncGroup,
  validateLegend,
} from '@/core';
import {
  useAppMetadataContext,
  useDataThresholds,
  useEventContext,
  useTimeRange,
  useVizFrameworkConfig,
  useVizPanelNotice,
} from '@/plugin-system';
import { useIsOnScreen } from '../../../plugin-system/runtime/in-view';
import {
  TimeSeriesDatasetResponse,
  useRenderSeriesActionFn,
  useScrollMarker,
  useTimeSeriesChartLegendProps,
  useTimeSeriesChartLegendSort,
  useTimeSeriesConfig,
  useVisualSettings,
  XAxisBounds,
} from './hooks';
import { useCorrectDataForLogAxis } from './hooks/log_axis';
import { useTimeSeriesLabelsToColumns } from './hooks/use-labels-to-columns';
import { useTimeSeriesQuickFilter } from './hooks/use-quick-filter';
import { useSelectedLegendItems } from './hooks/use-selected-legend-items';
import { useTimeSeriesLegendGroupBy } from './hooks/use-time-series-group-by';
import { useTimeSeriesToggleMode } from './hooks/use-toggle-mode';
import { TimeSeriesChartPanelContent, TimeSeriesChartPanelContentProps } from './TimeSeriesChartPanelContent';
import { DEFAULT_UNIT, TimeSeriesChartProps } from './time-series-chart-model';
import { convertPanelYAxis, getFallbackTimeScale, getLegendColumns } from './utils/data-transform';
import { getClickSeriesData } from './utils/model';
import { isOptimizedTooltipMode, useTooltipConfig } from './utils/tooltip-config';

type TimeSeriesChartPanelLoadedProps = TimeSeriesChartProps & {
  datasetResponse: TimeSeriesDatasetResponse;
  xAxisBounds?: XAxisBounds;
};

/**
 * Rendering of the time series chart panel when queries have been loaded and ECharts dataset is fully processed.
 */
export function TimeSeriesChartPanelLoaded(props: TimeSeriesChartPanelLoadedProps) {
  const {
    datasetResponse,
    width,
    height,
    legend: initLegendProps,
    onPluginSpecChange,
    thresholds: propsThresholds,
    yAxis,
    yAxisRight,
    tooltipMode,
    tooltipSort,
    enableTooltipPinning,
    showTooltipEndTime,
    showTooltipTotal,
    excludeEmptyResults,
    maxNearbySeries,
    enableTooltipPreciseValues,
    panelKey: initPanelKey,
    links,
    wrapSeriesName,
    onSeriesClick,
    onEventClick,
    overrides,
    barCategoryGap,
    renderSeriesActionTooltip,
    showQuickFilter,
    showToggleMode,
    onChangeMode,
    enableConsistentLegendSize,
    enableLabelsToColumns: initEnableLabelsToColumns,
    iconColumnHeaderName,
    disableListLegendItemTruncation,
    legendDensity,
    enableTableLegendColumnMenu,
    customTableLegendColumnsStart,
    customTableLegendColumnsEnd,
    pinnedTableLegendColumns: initPinnedTableLegendColumns = 'auto',
    getCustomLegendItemData,
    onAddFilters,
    layoutStrategy,
    collapsibleLegendProps,
    focusedSeriesId,
    tooltipContextMenu: tooltipContextMenuProps,
    filterLabelColumns,
    groupByProps: initGroupByProps,
    xAxisBounds,
    syncGroup: syncGroupProp,
    scrollMarkerTimestampMs,
  } = props;

  const queryTruncationSeriesLimit = useVizFrameworkConfig()?.seriesLimit;

  const {
    timeSeriesDataset,
    timeSeriesGrouping,
    timeScale,
    hasFormattedNames,
    hasSeriesIcons,
    totalValuePerTimestamp,
    tooltipContextMenuData,
    renderSeriesIconFns,
    commonLabels,
    totalSeries,
    totalDatapoints,
  } = datasetResponse;

  const eventCtx = useEventContext();
  const chartsTheme = useChartsTheme();
  const appMetadata = useAppMetadataContext();

  const resolvedSyncGroup = resolveSyncGroup(syncGroupProp, appMetadata?.app);

  const { setPanelNotice, deletePanelNotice } = useVizPanelNotice('sparse_series_render_limit');

  const visual = useVisualSettings();
  const { enableTimeSeriesChartAutoScaleStackedMin } = useFeatureFlags();

  // Key is used for unique legend items and for viewPanel query param in expand / drilldown flow
  let panelKey = useId('time-series-panel');
  if (initPanelKey !== undefined) {
    panelKey = initPanelKey;
  }

  const chartRef = useRef<ChartInstance>(null);

  const thresholds = useDataThresholds(propsThresholds, 'TimeSeries');

  const { toggleModeProps } = useTimeSeriesToggleMode({
    showToggleMode,
    // Override the specified initial legend mode to use `Table` when in expanded view.
    initMode: getLegendMode(initLegendProps?.mode),
    onChangeMode,
  });

  // Calling `getGroupKey` to remove it from `initLegendGroupByProps`.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { getGroupKey, getGroupLegendItem, ...initLegendGroupByProps } = initGroupByProps || {};
  const { groupByProps } = useTimeSeriesLegendGroupBy({
    ...initLegendGroupByProps,
    hasTimeSeriesGrouping: !!timeSeriesGrouping,
  });

  const legend: LegendSpecOptions | undefined = useMemo(() => {
    // If the legend is enabled, adjust it to use the mode from `useTimeSeriesToggleMode`.
    const legendProps = initLegendProps
      ? {
          ...initLegendProps,
          mode: toggleModeProps.mode,
        }
      : undefined;
    // Otherwise use legend from spec merged with default properties
    return legendProps && validateLegend(legendProps) ? merge({}, DEFAULT_LEGEND, legendProps) : undefined;
  }, [initLegendProps, toggleModeProps.mode]);

  const { enableLabelsToColumns } = useTimeSeriesLabelsToColumns({
    legendMode: legend?.mode,
    enableLabelsToColumns: initEnableLabelsToColumns,
    showToggleMode: toggleModeProps.show,
  });

  // Default unit fallback for left primary y axis
  let unit = yAxis?.unit ?? DEFAULT_UNIT;
  // When Percent stack is used, automatically set the unit to match
  if (visual.stack === 'Percent') {
    unit = {
      ...unit,
      kind: 'Percent',
    };
  }

  // convert native dashboard format to be ECharts compatible for primary yAxis
  const yAxisPrimaryConverted = useMemo(() => {
    return convertPanelYAxis(yAxis, thresholds, visual.stack, enableTimeSeriesChartAutoScaleStackedMin);
  }, [yAxis, thresholds, visual.stack, enableTimeSeriesChartAutoScaleStackedMin]);

  // convert native dashboard format to be ECharts compatible for alternative yAxis
  const yAxisAltConverted = useMemo(() => {
    const axis = convertPanelYAxis(yAxisRight);
    axis.position = 'right'; // https://echarts.apache.org/en/option.html#yAxis.position
    axis.unit = yAxisRight?.unit;
    // Do not show the split line for the alt y-axis
    axis.splitLine = { ...(axis.splitLine || {}), show: false };
    return axis;
  }, [yAxisRight]);

  const { absoluteTimeRange, setTimeRange } = useTimeRange();

  const { selectedLegendItems, setSelectedLegendItems } = useSelectedLegendItems({
    timeChartData: timeSeriesDataset,
    focusedItemId: focusedSeriesId,
    isGrouped: groupByProps?.isGrouped,
    timeSeriesGrouping,
  });

  const { quickFilter, quickFilterProps, filterSeriesFn } = useTimeSeriesQuickFilter({
    showQuickFilter,
  });

  // Lookup to know if an axis is using a logarithmic scale
  const isAxisLogScaleMapping = useMemo(
    () => [isLogScale(yAxis?.scale), isLogScale(yAxisRight?.scale)],
    [yAxis, yAxisRight]
  );

  const [shouldShowAllDataPoints, setShouldShowAllDataPoints] = useState(false);

  // Common labels should only affect tooltip series names when they will actually be
  // displayed. In single tooltip mode, common labels are hidden, so using condensedName
  // would remove labels with no way for the user to see them. Single mode can be triggered
  // either by the auto-optimization (series/datapoint limits) or by an explicit tooltipMode prop.
  const isSingleTooltipMode = tooltipMode === 'single' || isOptimizedTooltipMode(totalSeries, totalDatapoints);
  const tooltipCommonLabels = commonLabels && !isSingleTooltipMode ? commonLabels : undefined;

  // Populate series option data, legend items, other time series configuration from processed timeSeriesDataset.
  const {
    timeSeriesMapping,
    legendItems,
    hiddenDataPointCount,
    getSeriesById,
    anySeriesUsingRightAxis,
    rightYAxisIndex,
    datasetsUsedOnLogAxis,
    labelKeys,
    hasLabels,
  } = useTimeSeriesConfig({
    timeSeriesDataset,
    totalSeries,
    totalDatapoints,
    shouldShowAllDataPoints,
    visual,
    enableLabelsToColumns,
    legend,
    selectedLegendItems,
    quickFilter,
    thresholds,
    overrides,
    queryTruncationSeriesLimit,
    isAxisLogScaleMapping,
    barCategoryGap,
    yAxis,
    yAxisRight,
    filterSeriesFn,
    getCustomLegendItemData,
    renderSeriesIconFns,
    timeSeriesGrouping,
    isGrouped: groupByProps?.isGrouped,
    getGroupLegendItem,
    commonLabels: tooltipCommonLabels,
  });

  useCorrectDataForLogAxis(timeSeriesDataset, datasetsUsedOnLogAxis);

  // Translate the legend values into columns for the table legend.
  const { legendColumns, pinnedTableLegendColumns } = useMemo(() => {
    return getLegendColumns({
      values: legend?.values,
      unit,
      labelKeys,
      customTableLegendColumnsStart,
      customTableLegendColumnsEnd,
      onAddFilters,
      filterLabelColumns,
      pinnedTableLegendColumns: initPinnedTableLegendColumns,
    });
  }, [
    customTableLegendColumnsStart,
    customTableLegendColumnsEnd,
    labelKeys,
    legend?.values,
    unit,
    onAddFilters,
    filterLabelColumns,
    initPinnedTableLegendColumns,
  ]);

  // Show a warning when data is too sparse and let users know that data points are hidden to avoid performance impact
  useEffect(() => {
    if (hiddenDataPointCount && hiddenDataPointCount > 0) {
      setPanelNotice({
        type: 'warning',
        message: `${hiddenDataPointCount} additional data points not rendered.`,
        description: 'Displaying these data points could cause the browser to stall.',
        action: {
          label: 'Render additional data points',
          onClick: () => {
            setShouldShowAllDataPoints(true); // This will trigger a re-render and re-calculation
          },
          isDanger: true,
        },
      });
    } else {
      deletePanelNotice();
    }
  }, [hiddenDataPointCount, setPanelNotice, deletePanelNotice]);

  // override default spacing, see: https://echarts.apache.org/en/option.html#grid
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const gridOverrides: GridComponentOption = useMemo(() => {
    const gridXBase = 0;
    // Matches the line height of the y axis label.
    const labelWidth = 20;
    // Padding between the right side of the label and the left side of the chart.
    const labelChartPadding = chartsTheme.yAxis.label.chartPadding;

    const hasLeftLabel = yAxis?.label !== undefined && yAxis.label !== '';
    const hasRightLabel = yAxisRight?.label !== undefined && yAxisRight.label !== '';

    const gridLeft = hasLeftLabel ? gridXBase + labelWidth + labelChartPadding : gridXBase;
    const gridRight = anySeriesUsingRightAxis && hasRightLabel ? gridXBase + labelWidth + labelChartPadding : gridXBase;
    return {
      left: !yAxisPrimaryConverted.show ? gridXBase : gridLeft,
      right: !anySeriesUsingRightAxis ? gridXBase + 10 : gridRight, // Extra 10 to avoid clipping the last timestamp
      bottom: 0,
      // Adds spacing to the top if there are events so icons are not cut off
      top: eventCtx?.events?.length ? 24 : 4,
    };
  }, [
    chartsTheme.yAxis.label.chartPadding,
    yAxisPrimaryConverted.show,
    eventCtx?.events?.length,
    yAxis?.label,
    yAxisRight?.label,
    anySeriesUsingRightAxis,
  ]);

  const handleDataZoomDebounced = useDebounce((event: ZoomEventData) => {
    if (event) {
      const start = new Date(event.start);
      const end = new Date(event.end);
      const diffInMs = end.getTime() - start.getTime();
      const tenMinutesInMs = 10 * 60 * 1000;

      // Only clear seconds if the time range is more than 10 minutes
      const shouldClearSeconds = diffInMs > tenMinutesInMs;

      setTimeRange({
        start: shouldClearSeconds ? new Date(start.setSeconds(0, 0)) : start,
        end: shouldClearSeconds ? new Date(end.setSeconds(0, 0)) : end,
      });
    }
  }, 50);

  const handleClick = (params: MouseEventsParameters<unknown>) => {
    const { seriesType, seriesId, seriesIndex, data, dataIndex } = params;

    const isEvent = seriesType === 'scatter'; // we use scatter plot to render events
    const isTimeSeries = seriesType === 'line' || seriesType === 'bar';

    if (isEvent && data.event && onEventClick) {
      onEventClick(data.event as EventPayload);
    } else if (isTimeSeries && onSeriesClick && getSeriesById && seriesId) {
      const series = getSeriesById(seriesId);
      const clickSeriesData = getClickSeriesData({
        series,
        seriesIndex,
        dataIndex,
        showTooltipEndTime,
        timeChartData: timeSeriesDataset,
        timeRange: absoluteTimeRange,
        timeScale,
        data,
      });

      if (clickSeriesData) {
        onSeriesClick(clickSeriesData);
      }
    }
  };

  // Used to customize base chart when 'All' or 'Percent' stack is used.
  const isStacked = isStackEnabled(visual.stack);

  // Handler to get series name + series index inside tooltip SeriesInfo.tsx
  const tooltipConfig = useTooltipConfig({
    tooltipMode,
    sort: tooltipSort,
    maxNearbySeries,
    totalSeries,
    totalDatapoints,
    totalValuePerTimestamp,
    enableTooltipPreciseValues,
    enablePinning: enableTooltipPinning,
    showEndTime: showTooltipEndTime,
    showTotal: showTooltipTotal || (visual.stack === 'All' && totalSeries > 1),
    excludeEmptyResults,
    contextLinks: links,
    wrapSeriesName,
    rightYAxisIndex,
    getSeriesById,
    hasLogScale: isAxisLogScaleMapping.some((isLog) => isLog),
    isStacked,
    tooltipContextMenuProps,
    tooltipContextMenuData,
    appMetadata,
    panelKey,
    commonLabels,
  });

  // Manages legend sort state and syncing between view and edit modes
  const { sortModel, onSortModelChange } = useTimeSeriesChartLegendSort({
    legendSpec: legend,
    onPluginSpecChange,
  });

  const contentLegendProps = useTimeSeriesChartLegendProps({
    legend,
    legendColumns,
    customTableLegendColumnsStart,
    legendItems,
    chartRef,
    selectedLegendItems,
    setSelectedLegendItems,
    enableLabelsToColumns,
    hasFormattedNames,
    disableListLegendItemTruncation,
    hasSeriesIcons,
    iconColumnHeaderName,
    legendDensity,
    enableTableLegendColumnMenu,
    pinnedTableLegendColumns,
    timeSeriesGrouping,
    sortModel,
    onSortModelChange,
    hasLabels,
  });

  // Is this panel on screen?
  const onScreenCtx = useIsOnScreen();
  const isOnScreen = onScreenCtx === undefined ? true : onScreenCtx.isInView;

  const renderSeriesAction = useRenderSeriesActionFn({ getSeriesById, renderSeriesActionTooltip });

  const { seriesMapping: seriesMappingWithScrollMarker } = useScrollMarker({
    timeSeriesMapping,
    scrollMarkerTimestampMs,
  });

  const panelContentProps: TimeSeriesChartPanelContentProps = {
    chartRef: chartRef,
    dataset: timeSeriesDataset,
    events: eventCtx?.events ?? [],
    grid: gridOverrides,
    height: height,
    isStacked: isStacked,
    legendProps: contentLegendProps,
    legendSize: legend?.size,
    onDataZoom: handleDataZoomDebounced,
    seriesMapping: seriesMappingWithScrollMarker,
    syncGroup: isOnScreen ? resolvedSyncGroup : undefined,
    timeScale: timeScale || getFallbackTimeScale(),
    unit: unit,
    width: width,
    yAxis: yAxisPrimaryConverted,
    yAxisLabel: yAxis?.label,
    yAxisRight: anySeriesUsingRightAxis ? yAxisAltConverted : undefined,
    yAxisAltLabel: anySeriesUsingRightAxis ? yAxisRight?.label : undefined,
    onClick: handleClick,
    quickFilterProps: quickFilterProps,
    toggleModeProps: toggleModeProps,
    enableConsistentLegendSize: enableConsistentLegendSize,
    layoutStrategy: layoutStrategy,
    panelKey: panelKey,
    collapsibleLegendProps: collapsibleLegendProps,
    groupByProps: groupByProps,
    xAxisMinMs: xAxisBounds?.xAxisMinMs,
    xAxisMaxMs: xAxisBounds?.xAxisMaxMs,
    xAxisRangeMs: xAxisBounds?.xAxisRangeMs,
  };

  return (
    <SeriesActionProvider
      getSeriesById={getSeriesById}
      renderSeriesAction={renderSeriesActionTooltip ? renderSeriesAction : undefined}
    >
      <TimeSeriesTooltipProvider tooltipConfig={tooltipConfig}>
        <TimeSeriesChartPanelContent {...panelContentProps} />
      </TimeSeriesTooltipProvider>
    </SeriesActionProvider>
  );
}
